import { useState, type ReactNode } from "react";
import type { Vertical } from "@talenthop/shared";

/**
 * A skills-chip-style multi-select over one vertical's competency list — add/remove by clicking a
 * chip. Mirrors the server's own bounds (`workflowConfigSchema.competencies`,
 * packages/shared/src/schemas.ts): 1–12 selected, so the caller never builds a payload the API
 * would 400 on. Used wherever a requisition's assessed competencies are chosen or edited: the New
 * Request wizard's review step, the requisition "Edit Details" form, and the Questions modal's
 * first-time setup.
 *
 * `variant="add"` (#237/#238) shows only what is selected, each removable, plus an explicit
 * "+ Add competency" button that lists the rest of the vertical's competencies to add.
 */
export function CompetencyPicker({
  vertical,
  selected,
  onChange,
  max = 12,
  variant = "chips",
}: {
  vertical: Vertical | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
  max?: number;
  variant?: "chips" | "add";
}): ReactNode {
  const [adding, setAdding] = useState(false);
  const toggle = (id: string): void => {
    if (selected.includes(id)) {
      if (selected.length <= 1) return; // the server requires at least one when the key is sent
      onChange(selected.filter((c) => c !== id));
    } else {
      if (selected.length >= max) return;
      onChange([...selected, id]);
    }
  };

  const competencies = vertical?.competencies ?? [];
  if (competencies.length === 0) {
    return <p className="mut sm" style={{ margin: 0 }}>Pick a vertical first to see its competencies.</p>;
  }

  if (variant === "add") {
    const chosen = competencies.filter((c) => selected.includes(c.competencyId));
    const rest = competencies.filter((c) => !selected.includes(c.competencyId));
    const full = selected.length >= max;
    return (
      <div className="t1s-comp-add">
        <div className="t1s-comp-chips">
          {chosen.map((c) => (
            <span key={c.competencyId} className="t1s-comp-chip on">
              {c.label}
              <button
                type="button"
                className="t1s-comp-x"
                aria-label={`Remove ${c.label}`}
                data-tip={selected.length <= 1 ? "At least one competency is assessed" : "Remove"}
                data-tip-pos="top"
                disabled={selected.length <= 1}
                onClick={() => toggle(c.competencyId)}
              >
                ×
              </button>
            </span>
          ))}
          {/* Always shown, so it's clear adding exists — disabled (with the reason) when the vertical
              has nothing left to add; the API only accepts this vertical's own competencies. */}
          <button
            type="button"
            className="t1s-comp-addbtn"
            aria-expanded={adding}
            disabled={full || rest.length === 0}
            data-tip={
              rest.length === 0
                ? `All of ${vertical?.name ?? "this vertical"}'s competencies are already included`
                : full
                  ? `Up to ${max} at a time`
                  : undefined
            }
            data-tip-pos="top"
            onClick={() => setAdding((a) => !a)}
          >
            {adding && rest.length > 0 ? "Done" : "+ Add competency"}
          </button>
        </div>
        {rest.length === 0 && (
          <p className="mut sm t1s-comp-allnote">
            Every {vertical?.name ?? ""} competency is included. To assess different ones, change the
            assessment vertical above.
          </p>
        )}
        {adding && rest.length > 0 && (
          <div className="t1s-comp-chips t1s-comp-more" aria-label="Competencies you can add">
            {rest.map((c) => (
              <button
                key={c.competencyId}
                type="button"
                className="t1s-comp-chip"
                disabled={full}
                onClick={() => toggle(c.competencyId)}
              >
                + {c.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="t1s-comp-chips">
      {competencies.map((c) => {
        const on = selected.includes(c.competencyId);
        return (
          <button
            key={c.competencyId}
            type="button"
            className={`t1s-comp-chip${on ? " on" : ""}`}
            aria-pressed={on}
            onClick={() => toggle(c.competencyId)}
            data-tip={on ? "Click to remove" : selected.length >= max ? `Up to ${max} at a time` : "Click to include"}
            data-tip-pos="top"
            disabled={!on && selected.length >= max}
          >
            {on ? "✓" : "+"} {c.label}
          </button>
        );
      })}
    </div>
  );
}
